import "@sphtech/dmg-design-system/dist/theme-sww.css";
import "@sphtech/dmg-design-system/assets/sww/fonts.css";

import { buildUrlWithGARefCode } from "@app/utils/ga/tracking";
import Newsticker from "@components/Modules/Newsticker/Newsticker";
import { configFloatingCta, configSite, configTrackingCode } from "@pub/config";
import {
  BaseLayout,
  CustomClassContext,
  FloatingCta,
} from "@sphtech/dmg-design-system";
import { useLocation } from "react-router";

import {
  configHeader,
  svgHamburgerMenu,
  svgSearchIcon,
} from "./config/configHeader";
import customStyle from "./custom.module.css";
import footerSvgSource from "./invert-logo.svg?raw"; // Importing the inverted logo for the footer
import logoSvgSource from "./logo.svg?raw";
import { RoutePaths } from "./routePaths";

type SWWBaseLayoutProps = {
  ArticleTitle?: string;
  children: React.ReactNode;
  ads?: boolean;
  leaderboard?: boolean;
  skipMobileBanner?: boolean;
};

/**
 * Reusable Base layout specific to SWW.
 */
const SWWBaseLayout = ({
  ArticleTitle,
  children,
  ads = true,
  leaderboard = true,
  skipMobileBanner,
}: SWWBaseLayoutProps) => {
  const currentYear = new Date().getFullYear();
  const location = useLocation();

  const showFloatingCta =
    !!configFloatingCta.display &&
    !configFloatingCta.skipPaths?.includes(location.pathname);
  const floatingCtaProps = {
    ariaLabel: configFloatingCta.ariaLabel,
    placement: configFloatingCta.placement,
    target: configFloatingCta.target,
    mobileUpOnly: configFloatingCta.mobileUpOnly,
    href:
      (configFloatingCta.href &&
        configFloatingCta.refCode &&
        buildUrlWithGARefCode(
          configFloatingCta.href,
          configFloatingCta.refCode,
        )) ||
      configFloatingCta.href,
  };

  return (
    <CustomClassContext.Provider
      value={{
        navbar: {
          primaryItems: customStyle["primaryItemsContainer"],
        },
        homeContentBlockF: {
          anchorBlock: customStyle.homeContentBlockFAnchorBlock,
        },
        homeContentBlockD: {
          scroll: customStyle.homeContentBlockDscroll,
        },
        callBlock: {
          horizontalLine: customStyle.callBlockHorizontalLine,
        },
        homeLatestArticle: {
          item: customStyle.homeLatestArticleItem,
        },
        anchorBlock: {
          container: customStyle.anchorBlockContainer,
        },
        affiliateShopping: {
          blockContainer: customStyle.affiliateShoppingBlockContainer,
        },
        subAnchor: {
          item: customStyle.latestArticleContainer,
        },
      }}
    >
      <BaseLayout
        ArticleTitle={ArticleTitle}
        ads={ads}
        leaderboard={leaderboard}
        NewsTicker={<Newsticker />}
        RoutePaths={RoutePaths}
        svgSource={logoSvgSource}
        footerSvgSource={footerSvgSource}
        siteName={configSite.name}
        mciText={
          <span suppressHydrationWarning>
            Published by SPH Media Limited, Co. Regn. No. 202120748H. Copyright
            © {currentYear} SPH Media Limited. All rights reserved.
          </span>
        }
        socialIconProps={configHeader.socialMedia}
        megaMenuProps={{
          NewsLetterProps: {
            ...configHeader.megaMenu.newsletter,
            url:
              (configTrackingCode.signup &&
                buildUrlWithGARefCode(
                  configHeader.megaMenu.newsletter.url,
                  configTrackingCode.signup,
                )) ||
              configHeader.megaMenu.newsletter.url,
          },
          BannerProps: configHeader.megaMenu.banner,
          CloseButtonProps: configHeader.megaMenu.closeButton,
        }}
        currentLocation={location}
        FloatingCta={
          showFloatingCta &&
          (configFloatingCta.type === "image" ? (
            <FloatingCta
              {...floatingCtaProps}
              type="image"
              image={configFloatingCta.image}
            />
          ) : (
            <FloatingCta
              {...floatingCtaProps}
              type="button"
              text={configFloatingCta.text}
              variant={configFloatingCta.variant}
              icon={configFloatingCta.icon}
            />
          ))
        }
        skipMobileBanner={skipMobileBanner}
        svgHamburgerMenu={svgHamburgerMenu}
        svgSearchIcon={svgSearchIcon}
      >
        {children}
      </BaseLayout>
    </CustomClassContext.Provider>
  );
};

export default SWWBaseLayout;
